import React,{useState,useEffect,useCallback,useRef} from 'react';
import {useSiteContext} from '../../context/SiteContext';
import {motion,AnimatePresence} from 'framer-motion';
import StagedSubmitModal from '../ui/StagedSubmitModal';
import SafeIcon from '../../common/SafeIcon';
import activityTracker from '../../lib/activityTracker';

const DAYS_OF_WEEK=['Su','Mo','Tu','We','Th','Fr','Sa'];

const getDaysInMonth=(year,month)=> {
  return new Date(year,month+1,0).getDate();
};

const getFirstDayOfMonth=(year,month)=> {
  return new Date(year,month,1).getDay();
};

const formatDateDisplay=(dateStr)=> {
  if (!dateStr) return '';
  const [year,month,day]=dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const parseDateInput=(dateStr)=> {
  if (!dateStr) return null;
  const parts=dateStr.split('/');
  if (parts.length !==3) return null;
  const [day,month,year]=parts;
  return `${year}-${month.padStart(2,'0')}-${day.padStart(2,'0')}`;
};

const DatePicker=({value,onChange,onClose,isOpen,anchorRef})=> {
  const [viewDate,setViewDate]=useState(()=> {
    const now=new Date();
    return {year: now.getFullYear(),month: now.getMonth()};
  });
  const popoverRef=useRef(null);

  useEffect(()=> {
    if (value) {
      const [year,month]=value.split('-').map(Number);
      setViewDate({year,month: month-1});
    }
  },[value]);

  useEffect(()=> {
    if (!isOpen) return;

    const handleClickOutside=(e)=> {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape=(e)=> {
      if (e.key==='Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown',handleClickOutside);
    document.addEventListener('keydown',handleEscape);
    return ()=> {
      document.removeEventListener('mousedown',handleClickOutside);
      document.removeEventListener('keydown',handleEscape);
    };
  },[isOpen,onClose,anchorRef]);

  if (!isOpen) return null;

  const daysInMonth=getDaysInMonth(viewDate.year,viewDate.month);
  const firstDay=getFirstDayOfMonth(viewDate.year,viewDate.month);
  const today=new Date();
  const selectedDate=value ? new Date(value) : null;

  const handlePrevMonth=()=> {
    setViewDate((prev)=> {
      const newMonth=prev.month-1;
      if (newMonth < 0) {
        return {year: prev.year-1,month: 11};
      }
      return {...prev,month: newMonth};
    });
  };

  const handleNextMonth=()=> {
    setViewDate((prev)=> {
      const newMonth=prev.month+1;
      if (newMonth > 11) {
        return {year: prev.year+1,month: 0};
      }
      return {...prev,month: newMonth};
    });
  };

  const handleSelectDay=(day)=> {
    const year=viewDate.year;
    const month=String(viewDate.month+1).padStart(2,'0');
    const dayStr=String(day).padStart(2,'0');
    onChange(`${year}-${month}-${dayStr}`);
    onClose();
  };

  const handleClear=()=> {
    onChange('');
    onClose();
  };

  const isToday=(day)=> {
    return day===today.getDate() && viewDate.month===today.getMonth() && viewDate.year===today.getFullYear();
  };

  const isSelected=(day)=> {
    if (!selectedDate) return false;
    return day===selectedDate.getDate() && viewDate.month===selectedDate.getMonth() && viewDate.year===selectedDate.getFullYear();
  };

  const monthNames=['January','February','March','April','May','June','July','August','September','October','November','December'];

  return (
    <AnimatePresence>
      <motion.div
        ref={popoverRef}
        initial={{opacity: 0,scale: 0.95,y: -10}}
        animate={{opacity: 1,scale: 1,y: 0}}
        exit={{opacity: 0,scale: 0.95,y: -10}}
        transition={{duration: 0.2}}
        className="absolute z-30 top-full left-0 mt-2 p-4 rounded-2xl bg-[#1a1a24]/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] min-w-[300px]"
      >
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handlePrevMonth}
            aria-label="Previous month"
            className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <SafeIcon name="ChevronLeft" className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold text-white">
            {monthNames[viewDate.month]} {viewDate.year}
          </span>
          <button
            onClick={handleNextMonth}
            aria-label="Next month"
            className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <SafeIcon name="ChevronRight" className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS_OF_WEEK.map((day)=> (
            <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({length: firstDay}).map((_,i)=> (
            <div key={`empty-${i}`} className="p-1" />
          ))}
          {Array.from({length: daysInMonth}).map((_,i)=> {
            const day=i+1;
            const todayClass=isToday(day);
            const selectedClass=isSelected(day);
            return (
              <button
                key={day}
                onClick={()=> handleSelectDay(day)}
                className={`p-2 text-sm rounded-lg transition-all ${
                  selectedClass
                    ? 'bg-blue-600 text-white font-semibold'
                    : todayClass
                    ? 'bg-white/10 text-white border border-blue-400/30'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
          <button
            onClick={handleClear}
            className="text-xs text-slate-400 hover:text-white transition-colors"
          >
            Clear
          </button>
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const validatePhone=(phone)=> {
  if (!phone || phone.trim().length===0) return false;
  const cleaned=phone.replace(/[\s\-()]+/g,'');
  if (cleaned.length < 10) return false;
  if (/^0{3,}$/.test(cleaned) || /^1{3,}$/.test(cleaned)) return false;
  const auMobilePattern=/^(\+614|614|04)\d{8}$/;
  const digitsOnly=cleaned.replace(/^\+/,'');
  if (auMobilePattern.test(cleaned) || auMobilePattern.test(`+${digitsOnly}`)) return true;
  if (digitsOnly.length >=10 && digitsOnly.length <=12) return true;
  return false;
};

const validateEmail=(email)=> {
  if (!email || email.trim().length===0) return false;
  const trimmed=email.trim().toLowerCase();
  const invalidPatterns=['test','none','noemail','null','undefined','fake','spam'];
  if (invalidPatterns.some(p=> trimmed.includes(p))) return false;
  if (trimmed==='@gmail.com' || trimmed==='@gmail.co' || trimmed==='@yahoo.com') return false;
  const basicEmailPattern=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicEmailPattern.test(trimmed)) return false;
  const parts=trimmed.split('@');
  if (parts.length !==2) return false;
  const [local,domain]=parts;
  if (local.length < 2 || domain.length < 5) return false;
  if (!domain.includes('.')) return false;
  const domainParts=domain.split('.');
  const tld=domainParts[domainParts.length-1];
  if (tld.length < 2) return false;
  return true;
};

const ContactSection=()=> {
  const {selectedReference}=useSiteContext();
  const [formStartedAt,setFormStartedAt]=useState(null);
  const [submitStatus,setSubmitStatus]=useState('idle');
  const [submitError,setSubmitError]=useState('');
  const [softTimeoutMessage,setSoftTimeoutMessage]=useState('');
  const [datePickerOpen,setDatePickerOpen]=useState(false);
  const [notesExpanded,setNotesExpanded]=useState(false);
  const [fieldErrors,setFieldErrors]=useState({});
  const dateFieldRef=useRef(null);
  const notesFieldRef=useRef(null);
  const activeSubmissionRef=useRef(false);
  const submitTimeoutRef=useRef(null);
  const softTimeoutRef=useRef(null);
  const statusPollIntervalRef=useRef(null);
  const messageHandlerRef=useRef(null);
  const currentNonceRef=useRef('');
  const postMessageReceivedRef=useRef(false);

  const [formData,setFormData]=useState({
    fullName: '',
    phone: '',
    email: '',
    businessName: '',
    businessType: '',
    serviceArea: '',
    mainGoal: '',
    preferredContactDate: '',
    bestTimeToCall: 'Anytime',
    message: '',
    honeypot: '',
    consent: false,
  });

  const [formTouched,setFormTouched]=useState(false);

  useEffect(()=> {
    setFormStartedAt(Date.now());
  },[]);

  useEffect(()=> {
    if (!formTouched && Object.values(formData).some((v)=> v !=='' && v !==false)) {
      setFormTouched(true);
      activityTracker.trackFormStarted();
    }
  },[formData,formTouched]);

  const handleChange=useCallback((e)=> {
    const {name,value,type,checked}=e.target;
    setFormData((prev)=> ({...prev,[name]: type==='checkbox' ? checked : value}));
    if (fieldErrors[name]) {
      setFieldErrors((prev)=> ({...prev,[name]: ''}));
    }
  },[fieldErrors]);

  const handleDateChange=useCallback((dateValue)=> {
    setFormData((prev)=> ({...prev,preferredContactDate: dateValue}));
  },[]);

  const clearAllTimersAndListeners=useCallback(()=> {
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current=null;
    }
    if (softTimeoutRef.current) {
      clearTimeout(softTimeoutRef.current);
      softTimeoutRef.current=null;
    }
    if (statusPollIntervalRef.current) {
      clearInterval(statusPollIntervalRef.current);
      statusPollIntervalRef.current=null;
    }
    if (messageHandlerRef.current) {
      window.removeEventListener('message',messageHandlerRef.current);
      messageHandlerRef.current=null;
    }
  },[]);

  const cleanupSubmission=useCallback(()=> {
    activeSubmissionRef.current=false;
    postMessageReceivedRef.current=false;
    clearAllTimersAndListeners();
  },[clearAllTimersAndListeners]);

  const pollLeadStatus=useCallback(()=> {
    const cmsUrl=import.meta.env.VITE_BPAS_CMS_URL;
    if (!cmsUrl || !currentNonceRef.current || !activeSubmissionRef.current) return;

    const url=new URL(cmsUrl);
    url.searchParams.set('action','leadStatus');
    url.searchParams.set('submissionNonce',currentNonceRef.current);
    url.searchParams.set('clientTs',Date.now().toString());

    fetch(url.toString(),{
      method: 'GET',
      cache: 'no-store',
    })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then(data => {
      if (!activeSubmissionRef.current) return;
      
      if (data?.ok===true && data?.found===true && data?.status==='CAPTURED') {
        activeSubmissionRef.current=false;
        clearAllTimersAndListeners();
        setSubmitStatus('success');
        activityTracker.trackFormSuccess();
      }
    })
    .catch(()=> {});
  },[clearAllTimersAndListeners]);

  const handleSubmit=useCallback((e)=> {
    e.preventDefault();
    clearAllTimersAndListeners();

    const cmsUrl=import.meta.env.VITE_BPAS_CMS_URL;
    if (!cmsUrl) {
      setSubmitStatus('error');
      setSubmitError('The enquiry system is not connected yet. Please contact BPA Solutions directly.');
      activityTracker.trackFormError('CMS URL missing');
      return;
    }

    if (formData.honeypot) {
      setSubmitStatus('error');
      setSubmitError('We encountered an issue validating your submission. Please try again.');
      activityTracker.trackFormError('Honeypot triggered');
      return;
    }

    const timeOnForm=Date.now() - formStartedAt;
    if (timeOnForm < 3000) {
      setSubmitStatus('error');
      setSubmitError('Please take a moment to complete the form properly.');
      activityTracker.trackFormError('Submission too fast');
      return;
    }

    const cleanedPhone=formData.phone.replace(/[\s\-()]+/g,'');
    if (!validatePhone(formData.phone)) {
      setFieldErrors((prev)=> ({...prev,phone: 'Please enter a valid mobile number so we can send your confirmation SMS and call you back.'}));
      setSubmitStatus('error');
      setSubmitError('Please enter a valid mobile number so we can send your confirmation SMS and call you back.');
      activityTracker.trackFormError('Invalid phone number');
      return;
    }

    if (!validateEmail(formData.email)) {
      setFieldErrors((prev)=> ({...prev,email: 'Please enter a valid email address so we can send your enquiry confirmation and next steps.'}));
      setSubmitStatus('error');
      setSubmitError('Please enter a valid email address so we can send your enquiry confirmation and next steps.');
      activityTracker.trackFormError('Invalid email address');
      return;
    }

    if (!formData.consent) {
      setSubmitStatus('error');
      setSubmitError('Please agree to submit your enquiry and consent to receive communications.');
      activityTracker.trackFormError('Consent not given');
      return;
    }

    setSubmitStatus('processing');
    setSubmitError('');
    setSoftTimeoutMessage('');
    setFieldErrors({});
    activeSubmissionRef.current=true;
    postMessageReceivedRef.current=false;

    const submissionNonce=Math.random().toString(36).substring(2,15);
    currentNonceRef.current=submissionNonce;

    activityTracker.trackFormSubmitted({websiteId: selectedReference?.websiteId,businessType: formData.businessType});

    const leadPayload={
      type: 'lead',
      action: 'lead',
      fullName: formData.fullName,
      phone: cleanedPhone,
      email: formData.email.trim().toLowerCase(),
      businessName: formData.businessName,
      businessType: formData.businessType,
      serviceArea: formData.serviceArea,
      mainGoal: formData.mainGoal,
      preferredContactDate: formData.preferredContactDate,
      bestTimeToCall: formData.bestTimeToCall,
      selectedWebsiteReference: selectedReference?.publicTitle || '',
      selectedWebsiteId: selectedReference?.websiteId || '',
      selectedWebsiteCategory: selectedReference?.primaryCategory || '',
      selectedWebsiteOutcome: selectedReference?.outcomeType || '',
      selectedWebsiteURL: selectedReference?.previewUrl || selectedReference?.websiteUrl || '',
      message: formData.message,
      consentToContact: formData.consent,
      honeypot: formData.honeypot,
      formStartedAt: new Date(formStartedAt).toISOString(),
      submittedAt: new Date().toISOString(),
      submissionNonce,
      sessionId: localStorage.getItem('bpas_session_id') || '',
      sourcePage: window.location.pathname,
    };

    const handleMessage=(event)=> {
      if (!['https://script.google.com','https://script.googleusercontent.com'].includes(event.origin)) return;
      if (event.data?.source==='BPAS_LEAD_FORM') {
        const payload=event.data.payload || {};
        if (payload.submissionNonce && payload.submissionNonce !==currentNonceRef.current) {
          return;
        }
        postMessageReceivedRef.current=true;
        activeSubmissionRef.current=false;
        clearAllTimersAndListeners();
        if (payload.ok===true || payload.success===true) {
          setSubmitStatus('success');
          activityTracker.trackFormSuccess();
        } else {
          setSubmitStatus('error');
          setSubmitError(payload.message || 'We could not confirm the submission. Please contact BPA Solutions directly before resubmitting.');
          activityTracker.trackFormError(payload.message || 'System returned failure');
        }
      }
    };

    messageHandlerRef.current=handleMessage;
    window.addEventListener('message',handleMessage);

    softTimeoutRef.current=setTimeout(()=> {
      if (activeSubmissionRef.current && !postMessageReceivedRef.current) {
        setSoftTimeoutMessage('Still confirming your enquiry record...');
      }
    },10000);

    statusPollIntervalRef.current=setInterval(()=> {
      if (activeSubmissionRef.current && !postMessageReceivedRef.current) {
        pollLeadStatus();
      }
    },3000);

    submitTimeoutRef.current=setTimeout(()=> {
      if (activeSubmissionRef.current) {
        activeSubmissionRef.current=false;
        clearAllTimersAndListeners();
        setSubmitStatus('error');
        setSubmitError('We could not confirm the submission. Please contact BPA Solutions directly before resubmitting.');
        activityTracker.trackFormError('Submission timeout');
      }
    },75000);

    const form=document.createElement('form');
    form.method='POST';
    form.action=cmsUrl;
    form.target='bpasPortfolioLeadFrame';
    form.style.display='none';
    const input=document.createElement('input');
    input.type='hidden';
    input.name='payload';
    input.value=JSON.stringify(leadPayload);
    form.appendChild(input);
    document.body.appendChild(form);
    requestAnimationFrame(()=> {
      form.submit();
      setTimeout(()=> {
        if (document.body.contains(form)) {
          document.body.removeChild(form);
        }
      },100);
    });
  },[formData,formStartedAt,selectedReference,clearAllTimersAndListeners,pollLeadStatus]);

  const handleModalClose=()=> {
    if (submitStatus==='success') {
      setFormData((prev)=> ({...prev,message: '',consent: false}));
    }
    setSubmitStatus('idle');
    setSubmitError('');
    setSoftTimeoutMessage('');
    setFieldErrors({});
    cleanupSubmission();
  };

  useEffect(()=> {
    return ()=> {
      cleanupSubmission();
    };
  },[cleanupSubmission]);

  return (
    <section id="contact-section" className="py-24 bg-[#0a0a0e] relative">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <iframe
        id="bpasPortfolioLeadFrame"
        name="bpasPortfolioLeadFrame"
        title="BPAS Lead Frame"
        style={{display: 'none'}}
      />
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Request a Similar Website System</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Tell us what you want the website to achieve. We will review your business,service area and selected website reference before getting in touch.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-panel p-6 md:p-10 rounded-2xl space-y-6">
          <input
            type="text"
            id="honeypot"
            name="honeypot"
            value={formData.honeypot}
            onChange={handleChange}
            className="hidden"
            tabIndex="-1"
            autoComplete="off"
          />

          {selectedReference && (
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl mb-6 flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-400 font-semibold uppercase tracking-wider mb-1">Selected Reference</p>
                <p className="text-white font-medium">{selectedReference.publicTitle}</p>
              </div>
              <div className="px-3 py-1 bg-black/30 rounded text-xs text-slate-300 hidden sm:block">
                {selectedReference.businessType}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
              <input
                id="fullName"
                required
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">Mobile Number *</label>
              <input
                id="phone"
                required
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="0412 345 678"
                className={`w-full bg-[#1a1a24] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition-shadow ${
                  fieldErrors.phone ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-white/10 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              <p className="text-xs sm:text-sm leading-relaxed text-slate-400 mt-2 max-w-full break-words">
                Best mobile number for SMS confirmation and callback. Example: 0412 345 678.
              </p>
              {fieldErrors.phone && (
                <p className="text-xs text-red-400 mt-2">{fieldErrors.phone}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">Email Address *</label>
              <input
                id="email"
                required
                type="email"
                inputMode="email"
                autoComplete="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="name@business.com.au"
                className={`w-full bg-[#1a1a24] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition-shadow ${
                  fieldErrors.email ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-white/10 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              <p className="text-xs sm:text-sm leading-relaxed text-slate-400 mt-2 max-w-full break-words">
                We will send a copy of your enquiry and next steps to this email.
              </p>
              {fieldErrors.email && (
                <p className="text-xs text-red-400 mt-2">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-slate-300 mb-2">Business Name (Optional)</label>
              <input
                id="businessName"
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className="w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="businessType" className="block text-sm font-medium text-slate-300 mb-2">Business Type / Industry *</label>
              <input
                id="businessType"
                required
                type="text"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                className="w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow"
              />
            </div>
            <div>
              <label htmlFor="serviceArea" className="block text-sm font-medium text-slate-300 mb-2">Service Area / Location *</label>
              <input
                id="serviceArea"
                required
                type="text"
                name="serviceArea"
                value={formData.serviceArea}
                onChange={handleChange}
                className="w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow"
              />
            </div>
          </div>

          <div>
            <label htmlFor="mainGoal" className="block text-sm font-medium text-slate-300 mb-2">Main Goal / Outcome Needed *</label>
            <select
              id="mainGoal"
              required
              name="mainGoal"
              value={formData.mainGoal}
              onChange={handleChange}
              className="w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none transition-shadow"
            >
              <option value="">Select a goal...</option>
              <option value="Lead Generation & Enquiries">Lead Generation & Enquiries</option>
              <option value="Professional Presentation">Professional Presentation</option>
              <option value="Automated Booking System">Automated Booking System</option>
              <option value="Product Showcase">Product Showcase</option>
              <option value="System Integration (CRM,Booking,etc)">System Integration (CRM,Booking,etc)</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label htmlFor="preferredContactDate" className="block text-sm font-medium text-slate-300 mb-2">Preferred Contact Date (Optional)</label>
              <div ref={dateFieldRef} className="relative">
                <input
                  id="preferredContactDate"
                  type="text"
                  name="preferredContactDate"
                  value={formatDateDisplay(formData.preferredContactDate)}
                  onChange={(e)=> {
                    const parsed=parseDateInput(e.target.value);
                    handleDateChange(parsed || '');
                  }}
                  onFocus={()=> setDatePickerOpen(true)}
                  readOnly
                  placeholder="Select preferred date"
                  className="w-full bg-[#1a1a24] border border-white/10 rounded-xl pl-4 pr-12 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow cursor-pointer"
                />
                <button
                  type="button"
                  onClick={()=> setDatePickerOpen(!datePickerOpen)}
                  aria-label="Select preferred contact date"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-blue-400 transition-colors"
                >
                  <SafeIcon name="Calendar" className="w-5 h-5" />
                </button>
                <DatePicker
                  value={formData.preferredContactDate}
                  onChange={handleDateChange}
                  onClose={()=> setDatePickerOpen(false)}
                  isOpen={datePickerOpen}
                  anchorRef={dateFieldRef}
                />
              </div>
            </div>
            <div>
              <label htmlFor="bestTimeToCall" className="block text-sm font-medium text-slate-300 mb-2">Best Time to Call *</label>
              <select
                id="bestTimeToCall"
                required
                name="bestTimeToCall"
                value={formData.bestTimeToCall}
                onChange={handleChange}
                className="w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none transition-shadow"
              >
                <option value="Anytime">Anytime</option>
                <option value="Morning">Morning</option>
                <option value="Afternoon">Afternoon</option>
                <option value="Evening">Evening</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="message" className="block text-sm font-medium text-slate-300">Additional Message (Optional)</label>
              <button
                type="button"
                onClick={()=> setNotesExpanded(!notesExpanded)}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
              >
                {notesExpanded ? 'Compact' : 'Expand'}
              </button>
            </div>
            <textarea
              id="message"
              name="message"
              ref={notesFieldRef}
              rows="5"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us anything else that may help us understand your website goals..."
              className={`w-full bg-[#1a1a24] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y transition-all ${
                notesExpanded ? 'min-h-[320px] max-h-[480px]' : 'min-h-[160px] max-h-[360px]'
              }`}
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center justify-center mt-1">
              <input
                required
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="peer sr-only"
              />
              <div className="w-5 h-5 border-2 border-slate-600 rounded peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all"></div>
              <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" viewBox="0 0 14 10" fill="none">
                <path d="M1 5L4.5 8.5L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
              I agree to submit my enquiry to BPA Solutions,and I consent to receive relevant BPA Solutions tips,updates,and service information. I understand I can unsubscribe at any time.
            </span>
          </label>

          <button
            type="submit"
            disabled={submitStatus==='processing'}
            className="w-full py-4 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 text-white rounded-xl font-bold text-lg transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-[#13131a]"
          >
            {submitStatus==='processing' ? 'Submitting...' : 'Submit Enquiry'}
          </button>
        </form>
      </div>
      <StagedSubmitModal
        status={submitStatus}
        errorMessage={submitError}
        softTimeoutMessage={softTimeoutMessage}
        onClose={handleModalClose}
      />
    </section>
  );
};

export default ContactSection;